import { Feature } from "geojson";
import i18n from "i18n";

import { Floors } from "../../redux/services/config";
import { useAppSelector } from "../../redux/hooks";
import { TransportFeed } from "../../redux/types";

import { buildRouteShortlink, parseOpeningTimeString } from "../../utils";
import { getFloorDifference } from "../../utils/getFloorDifference";
import { parseLanguageObject } from "utils/parseLanguageObject";

import TransportSheet from "../../components/Map/components/TransportSheet/TransportSheet";
import AssetInformation from "../../components/Map/components/AssetInformation/AssetInformation";

interface Props {
  className?: string;
  defaultFloorId: number;
  floors: Floors;
  kilometres?: number;
  onClose?: () => void;
  selectedFeature: Feature | null;
  transportFeeds?: TransportFeed[];
  showNearestLabel?: boolean;
  showQRCode?: boolean;
  transportFeedsIsError: boolean;
  resetTimeouts?: () => void;
  fromCarousel?: boolean;
}
export default function SheetContainer({
  defaultFloorId,
  floors,
  selectedFeature,
  transportFeeds,
  transportFeedsIsError = false,
  showQRCode = true,
  fromCarousel,
  ...props
}: Props) {
  const { isStepFreeRoute } = useAppSelector((state) => state.application);

  if (!selectedFeature) return null;

  const operatingHours = selectedFeature.properties?.opening_times || null;
  const qrCodeLink = buildRouteShortlink(selectedFeature.id, isStepFreeRoute);
  const formattedOperatingHours =
    typeof operatingHours === "string" || operatingHours === null
      ? parseOpeningTimeString(operatingHours)
      : operatingHours;
  const { amount, direction } = getFloorDifference(
    selectedFeature.properties?.poi_floor_id ||
      selectedFeature.properties?.floor_id,
    defaultFloorId,
    floors,
  );
  const featureTransportAPI = selectedFeature.properties?.transport_api;
  const transportFeedReady =
    transportFeeds &&
    featureTransportAPI &&
    transportFeeds[0].resultOk !== undefined;
  const floorName = selectedFeature.properties?.floor_name || "";

  // Handle both array format and stringified JSON format
  let parsedFloorName = floorName;

  if (Array.isArray(floorName)) {
    // Already an array of language objects
    parsedFloorName = parseLanguageObject(floorName, i18n.language);
  } else if (typeof floorName === "string" && floorName.startsWith("[")) {
    // Stringified JSON array - parse it first
    try {
      const parsedArray = JSON.parse(floorName);
      if (Array.isArray(parsedArray)) {
        parsedFloorName = parseLanguageObject(parsedArray, i18n.language);
      }
    } catch (e) {
      // If parsing fails, use the string as-is
      parsedFloorName = floorName;
    }
  }
  // If it's just a regular string, use it as-is

  const { popup_subheader, popup_header } = selectedFeature.properties || {};

  const sheetProps = {
    ...props,
    qrCodeLink,
    showQRCode,
    floorChange: { amount, direction },
    category: Array.isArray(popup_subheader)
      ? parseLanguageObject(popup_subheader, i18n.language)
      : popup_subheader,
    name: Array.isArray(popup_header)
      ? parseLanguageObject(popup_header, i18n.language)
      : popup_header,
  };

  return selectedFeature.properties?.transport_api ? (
    <TransportSheet
      {...sheetProps}
      dataQA="transport-information"
      {...(transportFeedReady && { transportFeeds })}
      transportFeedsIsError={transportFeedsIsError}
    />
  ) : (
    <AssetInformation
      {...sheetProps}
      dataQA="asset-information"
      building={selectedFeature.properties?.location_name}
      operatingHours={formattedOperatingHours}
      floorName={parsedFloorName}
      isTemporarilyClosed={selectedFeature.properties?.temporarily_closed}
      imageSrc={selectedFeature.properties?.popup_image_url}
      fromCarousel={fromCarousel}
    />
  );
}
